/**
 * app.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import 'core-js/stable';

// Polyfill intersection-observer for IE
import 'intersection-observer';

import 'sanitize.css/sanitize.css';

import { AppState, Auth0Provider } from '@auth0/auth0-react';
import chmln from '@chamaeleonidae/chmln';
import { extraErrorDataIntegration } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from 'Containers/App';
import { ContentfulType } from 'ContentfulDefaults/ContentfulType';
import FeatureFlagProvider from 'Contexts/featureFlagContext';
import FederalLimitsProvider from 'Contexts/federalLimitsContext';
import { ProgressMap } from 'Contexts/profileContext';
import ScrollToTop from 'Shared/scrollToTop';

import { withResources } from './configureApp';
import { configureReduxStore } from './configureReduxStore';
import { DEPLOY_ENVIRONMENTS, SENTRY_IGNORE } from './constants';
import { GlobalReducerState } from './reducers';
import { datadogEnv, initDatadogLogs } from './utils/datadogLogs';

declare global {
  interface Window {
    _env_: {
      INSIGHTS_URL?: string;
      INSIGHTS_API_KEY?: string;
      DEPLOY_ENV?: DEPLOY_ENVIRONMENTS;
      REACT_APP_AUTH0_CLIENT_ID?: string;
      REACT_APP_AUTH0_DOMAIN?: string;
      REACT_APP_AUTH0_AUDIENCE?: string;
      REACT_APP_DOC_FINDER_URL?: string;
      REACT_APP_CHAMELEON_TOKEN?: string;
      REACT_APP_USERSNAP_TOKEN?: string;
      REACT_APP_CHAT_NG_PROD_URL?: string;
      REACT_APP_CHAT_NG_STAGING_URL?: string;
      REACT_APP_DD_RUM_APPLICATION_ID?: string;
      REACT_APP_DD_RUM_CLIENT_TOKEN?: string;
      REACT_APP_DD_RUM_SESSION_SAMPLE_RATE?: string;
    };
    DD_RUM?: {
      init: (config: Record<string, unknown>) => void;
      addAction: (name: string, context?: Record<string, unknown>) => void;
      setUser: (user: Record<string, unknown>) => void;
    };
    DD_LOGS?: {
      onReady: (callback: () => void) => void;
      init: (config: Record<string, unknown>) => void;
      logger: { info: (message: string, context?: Record<string, unknown>) => void };
      setGlobalContextProperty: (key: string, value: unknown) => void;
    };
  }
}

const clientId = window._env_.REACT_APP_AUTH0_CLIENT_ID || '';
const domain = window._env_.REACT_APP_AUTH0_DOMAIN || '';
const audience = window._env_.REACT_APP_AUTH0_AUDIENCE || '';

// Get the redirect URI - use only origin since Auth0 only allows domain-level callback URLs
// The actual return path is preserved via appState.returnTo in loginWithRedirect()
// and handled by onRedirectCallback after authentication
const getRedirectUri = (): string => {
  return window.location.origin;
};

const onRedirectCallback = (appState?: AppState): void => {
  window.onbeforeunload = null; // Removes the page exit protection
  // Temp solution enforcing redirect after login because of the many sideEffects that might fail and route us elsewhere,
  // during page load
  // For example, fetching data from contentful in TextProvider/fetching config with route param, etc..
  // so here just "catch immediately" and redirect
  const targetUrl = appState && appState.returnTo ? appState.returnTo : window.location.pathname;
  window.location.assign(targetUrl);
};

const deployEnvironment = window._env_.DEPLOY_ENV || DEPLOY_ENVIRONMENTS.DEV;

Sentry.init({
  dsn: 'https://4c587ac0d37c4067b0078ef872079230@o31114.ingest.sentry.io/5711627',
  environment: deployEnvironment,
  integrations: [extraErrorDataIntegration()],
  ignoreErrors: SENTRY_IGNORE,
});

const ddAppId = window._env_.REACT_APP_DD_RUM_APPLICATION_ID;
const ddClientToken = window._env_.REACT_APP_DD_RUM_CLIENT_TOKEN;

if (ddAppId && ddClientToken) {
  try {
    window.DD_RUM?.init({
      applicationId: ddAppId,
      clientToken: ddClientToken,
      site: 'us3.datadoghq.com',
      service: 'alex-go',
      env: deployEnvironment,
      sessionSampleRate: Number(window._env_.REACT_APP_DD_RUM_SESSION_SAMPLE_RATE) || 0,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      remoteConfigurationEnabled: true,
    });
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
}

// Logs need only the client token, so init is independent of the RUM block above.
if (ddClientToken) {
  initDatadogLogs({
    clientToken: ddClientToken,
    env: datadogEnv(deployEnvironment, window.location.hostname),
  });
}

// Init Chameleon
chmln.init(window._env_.REACT_APP_CHAMELEON_TOKEN, {
  fastUrl: 'https://fast.chameleon.io/',
});

const MOUNT_NODE = document.getElementById('app');

// Create redux store with history
const appStateJson = localStorage.getItem('store');
const contentfulJson = localStorage.getItem('contentful');
const profileProgressJson = localStorage.getItem('profileMap');

const initialTextMap: ContentfulType = JSON.parse(contentfulJson || '{}');
const initialProfileMap: ProgressMap | Record<string, never> = JSON.parse(profileProgressJson || '{}');
const initialState: GlobalReducerState | Record<string, never> = JSON.parse(appStateJson || '{}');

const store = configureReduxStore(initialState, deployEnvironment);
const getStore = () => store.getState() as GlobalReducerState;

const render = () => {
  if (!MOUNT_NODE || !store) {
    return;
  }
  const root = createRoot(MOUNT_NODE);
  root.render(
    withResources(
      <BrowserRouter>
        <ScrollToTop>
          <FeatureFlagProvider searchParams={new URL(document.location.href).searchParams}>
            <FederalLimitsProvider>
              <Auth0Provider
                domain={domain}
                clientId={clientId}
                issuer={domain}
                cacheLocation="localstorage"
                authorizationParams={{
                  redirect_uri: getRedirectUri(),
                  audience,
                }}
                onRedirectCallback={onRedirectCallback}
              >
                <App
                  initialTextMap={initialTextMap as never}
                  initialProfileMap={initialProfileMap as never}
                  getStore={getStore as never}
                />
              </Auth0Provider>
            </FederalLimitsProvider>
          </FeatureFlagProvider>
        </ScrollToTop>
      </BrowserRouter>,
      store,
    ),
  );

  // Remove the HTML preloader spinner after React mounts
  const preloader = document.getElementById('preloader');
  if (preloader) {
    preloader.style.opacity = '0';
    preloader.style.transition = 'opacity 0.1s ease';
    setTimeout(() => preloader.remove(), 300); // fully remove after fade-out
  }
};

render();
